import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Loader2, CheckCircle2, Wifi, MessageSquare, ListChecks } from "lucide-react";

// Shown after login when the user has a saved WhatsApp session that is being
// reconnected. Polls /whatsapp/status, walks the user through visible stages,
// and auto-dismisses ~3.5s after connection completes (enough time for the
// backfill ingest to start populating tasks). Hidden permanently once dismissed
// in this browser session — short-term disconnects mid-session show only the
// small sidebar indicator, not this big overlay.

const SESSION_KEY = "brainydo_reconnect_complete";

export default function ReconnectingOverlay() {
  const [phase, setPhase] = useState(() =>
    sessionStorage.getItem(SESSION_KEY) ? "done" : "idle"
  );
  const connectedAtRef = useRef(null);

  useEffect(() => {
    if (phase === "done") return;
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get("/whatsapp/status");

        // No saved session — user must scan QR. Don't block the UI.
        if (!data.has_auth) return setPhase("idle");

        // Mid-pair (QR shown) — let WhatsApp Connect page handle the flow.
        if (data.status === "qr" || data.qr_image) return setPhase("idle");

        // Bridge offline — nothing we can do, hide the overlay.
        if (data.status === "bridge_offline") return setPhase("idle");

        // Jalpi (WABA) provider has no bridge session to reconnect — never applicable.
        if (data.status === "jalpi") return setPhase("idle");

        if (data.connected) {
          if (!connectedAtRef.current) {
            connectedAtRef.current = Date.now();
            setPhase("syncing");
          } else if (Date.now() - connectedAtRef.current > 3500) {
            sessionStorage.setItem(SESSION_KEY, "1");
            setPhase("done");
          }
        } else {
          setPhase("reconnecting");
          connectedAtRef.current = null;
        }
      } catch {
        // Backend hiccup — never block UI on overlay errors
        setPhase("idle");
      }
    };

    tick();
    const id = setInterval(tick, 1500);
    return () => { cancelled = true; clearInterval(id); };
  }, [phase]);

  if (phase === "idle" || phase === "done") return null;

  const syncing = phase === "syncing";

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/85 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full sw-fade-up">
        <div className="text-center mb-6">
          <div
            className={
              "w-20 h-20 mx-auto mb-4 rounded-full grid place-items-center border " +
              (syncing
                ? "bg-emerald-50 border-emerald-200"
                : "bg-blue-50 border-blue-200")
            }
          >
            {syncing ? (
              <CheckCircle2 size={36} className="text-emerald-600" />
            ) : (
              <Loader2 size={36} className="text-blue-600 animate-spin" />
            )}
          </div>
          <h2 className="text-[20px] font-bold text-slate-900 mb-1">
            {syncing ? "Welcome back!" : "Reconnecting your WhatsApp"}
          </h2>
          <p className="text-[13px] text-slate-500 px-2">
            {syncing
              ? "Catching up on messages and tasks you missed."
              : "Restoring your secure session — this takes a few seconds."}
          </p>
        </div>

        <div className="space-y-2.5 bg-slate-50 rounded-xl p-4 border border-slate-200">
          <Step done icon={<Wifi size={13} />} label="Saved session found" />
          <Step
            done={syncing}
            active={!syncing}
            icon={<Wifi size={13} />}
            label="Connecting to WhatsApp"
          />
          <Step
            active={syncing}
            icon={<MessageSquare size={13} />}
            label="Syncing recent messages"
          />
          <Step
            active={syncing}
            icon={<ListChecks size={13} />}
            label="Updating your tasks"
          />
        </div>

        <p className="text-[11px] text-slate-400 text-center mt-4">
          You can wait here or close this tab — your data will keep syncing.
        </p>
      </div>
    </div>
  );
}

function Step({ done, active, icon, label }) {
  return (
    <div className="flex items-center gap-3">
      <div
        className={
          "w-6 h-6 rounded-full grid place-items-center shrink-0 " +
          (done
            ? "bg-emerald-100"
            : active
            ? "bg-blue-100"
            : "bg-slate-200/70")
        }
      >
        {done ? (
          <CheckCircle2 size={13} className="text-emerald-600" />
        ) : active ? (
          <Loader2 size={11} className="text-blue-600 animate-spin" />
        ) : (
          <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
        )}
      </div>
      <span
        className={
          "shrink-0 " +
          (done ? "text-emerald-600" : active ? "text-blue-600" : "text-slate-400")
        }
      >
        {icon}
      </span>
      <span
        className={
          "text-[13px] " +
          (done || active ? "text-slate-800 font-medium" : "text-slate-500")
        }
      >
        {label}
      </span>
    </div>
  );
}
